import { create } from "zustand";
import { persist } from "zustand/middleware";
import { DEFAULT_PLATFORM_OPERATORS } from "@/domain/platformPermissions";
import { showToastI18n } from "@/lib/toastI18n";

const defaultModules = {
  phase1: true,
  phase2: true,
  phase3: true,
  phase4: true,
  phase5: true,
};

const limitedModules = {
  phase1: true,
  phase2: false,
  phase3: false,
  phase4: false,
  phase5: false,
};

const DEFAULT_CUSTOMERS = [
  {
    id: "sc-dev-gps",
    name: "GPS WALA consolidated transport group",
    shortName: "Developer GPS",
    status: "active",
    plan: "Enterprise",
    userLimit: 50,
    modules: { ...defaultModules },
    createdAt: "2025-06-01T00:00:00Z",
  },
  {
    id: "sc-harbor-line",
    name: "Harbor Line Transport",
    shortName: "Harbor Line",
    status: "trial",
    plan: "Starter",
    userLimit: 8,
    modules: { ...limitedModules },
    createdAt: "2026-09-15T00:00:00Z",
  },
];

const DEFAULT_CUSTOMER_USERS = [
  {
    id: "cu-priya",
    customerId: "sc-dev-gps",
    name: "Priya Sharma",
    email: "priya@developer-gps.example",
    role: "group_admin",
    inviteStatus: "active",
    lastInviteAt: null,
  },
  {
    id: "cu-harbor-admin",
    customerId: "sc-harbor-line",
    name: "Vikram Nair",
    email: "admin@harbor-line.example",
    role: "group_admin",
    inviteStatus: "active",
    lastInviteAt: null,
  },
  {
    id: "cu-harbor-invited",
    customerId: "sc-harbor-line",
    name: "Pending planner",
    email: "planner@harbor-line.example",
    role: "planner",
    inviteStatus: "invited",
    lastInviteAt: "2026-10-01T10:00:00Z",
  },
];

const usePlatformStore = create()(
  persist(
    (set, get) => ({
      operators: DEFAULT_PLATFORM_OPERATORS,
      customers: DEFAULT_CUSTOMERS,
      customerUsers: DEFAULT_CUSTOMER_USERS,
      getCustomer: (id) => get().customers.find((c) => c.id === id),
      getCustomerUsers: (customerId) =>
        get().customerUsers.filter((u) => u.customerId === customerId),
      addCustomer: (payload) => {
        const id = `sc-${Math.random().toString(36).slice(2, 8)}`;
        const customer = {
          id,
          status: "trial",
          plan: "Starter",
          userLimit: 10,
          modules: {
            phase1: true,
            phase2: false,
            phase3: false,
            phase4: false,
            phase5: false,
          },
          createdAt: new Date().toISOString(),
          ...payload,
        };
        set((s) => ({ customers: [...s.customers, customer] }));
        showToastI18n("toasts.created", "success");
        return customer;
      },
      updateCustomer: (id, patch) => {
        set((s) => ({
          customers: s.customers.map((c) =>
            c.id === id ? { ...c, ...patch } : c,
          ),
        }));
        showToastI18n("toasts.saved", "success");
      },
      setCustomerModules: (id, modules) => {
        set((s) => ({
          customers: s.customers.map((c) =>
            c.id === id ? { ...c, modules: { ...modules } } : c,
          ),
        }));
        showToastI18n("toasts.updated", "success");
      },
      toggleCustomerModule: (id, key) => {
        const customer = get().getCustomer(id);
        if (!customer) return;
        get().setCustomerModules(id, {
          ...customer.modules,
          [key]: !customer.modules[key],
        });
      },
      inviteCustomerUser: (customerId, user) => {
        const customer = get().getCustomer(customerId);
        if (!customer) return { ok: false, reason: "missing_customer" };
        const existing = get().customerUsers.filter(
          (u) => u.customerId === customerId,
        );
        if (existing.length >= customer.userLimit) {
          return { ok: false, reason: "user_limit" };
        }
        const row = {
          id: `cu-${Math.random().toString(36).slice(2, 8)}`,
          customerId,
          inviteStatus: "invited",
          lastInviteAt: new Date().toISOString(),
          ...user,
        };
        set((s) => ({ customerUsers: [...s.customerUsers, row] }));
        showToastI18n("toasts.created", "success");
        return { ok: true, user: row };
      },
      resetCustomerUserPassword: (userId) => {
        set((s) => ({
          customerUsers: s.customerUsers.map((u) =>
            u.id === userId
              ? {
                  ...u,
                  inviteStatus: "reset_pending",
                  lastInviteAt: new Date().toISOString(),
                }
              : u,
          ),
        }));
        showToastI18n("toasts.updated", "success");
      },
      getOperatorByEmail: (email) => {
        const normalized = email.toLowerCase();
        const seed = DEFAULT_PLATFORM_OPERATORS.find(
          (o) => o.email.toLowerCase() === normalized && o.active,
        );
        const fromState = get().operators.find(
          (o) => o.email.toLowerCase() === normalized && o.active,
        );
        if (fromState && seed) {
          return { ...seed, ...fromState, roleId: seed.roleId };
        }
        return fromState ?? seed;
      },
    }),
    { name: "gps-erp-next-platform" },
  ),
);

export { usePlatformStore };
